import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ElementRef,
  ViewChild,
  HostListener,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { FormBuilderV2StateService } from '../../services/form-builder-v2-state.service';
import {
  FB_V2_SIDEBAR_MIN,
  FB_V2_SIDEBAR_MAX,
} from '../../models/form-builder-v2.models';

@Component({
  selector: 'irembogov-fb-resizable-layout',
  templateUrl: './fb-resizable-layout.component.html',
  styleUrls: ['./fb-resizable-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FbResizableLayoutComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  sidebarWidth = 30;
  sidebarCollapsed = false;
  isResizing = false;
  readonly sidebarMin = FB_V2_SIDEBAR_MIN;
  readonly sidebarMax = FB_V2_SIDEBAR_MAX;
  readonly dividerWidth = 6;
  // Computed so the layout fills the space from its top to the viewport bottom,
  // keeping the sidebar footer (Continue button) on-screen regardless of how
  // much header/step/toolbar content sits above the builder.
  layoutHeight: number | null = null;

  private destroy$ = new Subject<void>();
  @ViewChild('layoutContainer', { static: true }) layoutContainer!: ElementRef;

  constructor(
    private stateService: FormBuilderV2StateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.stateService.sidebarWidth$
      .pipe(takeUntil(this.destroy$))
      .subscribe(width => {
        this.sidebarWidth = width;
        this.cdr.markForCheck();
      });

    this.stateService.sidebarCollapsed$
      .pipe(takeUntil(this.destroy$))
      .subscribe(collapsed => {
        this.sidebarCollapsed = collapsed;
        this.cdr.markForCheck();
      });
  }

  ngAfterViewInit(): void {
    // Defer so the surrounding layout has settled before measuring.
    setTimeout(() => this.updateLayoutHeight(), 0);
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateLayoutHeight();
  }

  private updateLayoutHeight(): void {
    const el = this.layoutContainer?.nativeElement as HTMLElement | undefined;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    // Fill the viewport so the builder uses the full screen. The "Continue"
    // action bar sits just below the fold and is revealed by scrolling — it is
    // not reserved/pinned.
    const bottomGap = 12;
    this.layoutHeight = Math.max(360, window.innerHeight - top - bottomGap);
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onResizeStart(): void {
    this.isResizing = true;
  }

  onResizeCommitted(width: number): void {
    this.isResizing = false;
    this.stateService.setSidebarWidth(width, true);
  }

  onDividerDblClick(): void {
    this.stateService.toggleSidebarCollapse();
  }

  get canvasWidth(): string {
    if (this.sidebarCollapsed) return '100%';
    // Subtract the 6px divider so canvas + divider + sidebar == 100% exactly.
    // Otherwise the total overflows by the divider width and the sidebar's right
    // edge is clipped by the layout's overflow:hidden.
    return `calc(${100 - this.sidebarWidth}% - ${this.dividerWidth}px)`;
  }

  get sidebarWidthStyle(): string {
    if (this.sidebarCollapsed) return '0%';
    return `${this.sidebarWidth}%`;
  }

  get isCompact(): boolean {
    return this.sidebarWidth < 30;
  }
}
